import { Injectable, inject } from '@angular/core';
import { NavigationTarget } from '../navigation-target';
import { ReturnUrlPolicy } from './return-url.policy';

/**
 * Bridges the deeplink across the Okta redirect (same tab) until it is consumed once on the landing route.
 * The welcome page URL (`?returnUrl=`) is the source of truth: every visit of the welcome page
 * replaces the stored deeplink, so an abandoned one never outlives the next entry into the portal.
 */
@Injectable({ providedIn: 'root' })
export class ReturnUrlStore {
  private static readonly storageKey = 'portal.returnUrl';
  private readonly policy = inject(ReturnUrlPolicy);

  /** Whether the URL is a safe deeplink. */
  accepts(url: string | null | undefined): url is string {
    return !!url && !!this.policy.toTarget(url);
  }

  /** Takes over the deeplink of an entry into the portal: stores a safe one, otherwise forgets the previous one. */
  capture(url: string | null | undefined): void {
    if (this.accepts(url)) {
      sessionStorage.setItem(ReturnUrlStore.storageKey, url);
    } else {
      this.clear();
    }
  }

  consume(): NavigationTarget | null {
    const url = sessionStorage.getItem(ReturnUrlStore.storageKey);
    this.clear();
    return url ? this.policy.toTarget(url) : null;
  }

  clear(): void {
    sessionStorage.removeItem(ReturnUrlStore.storageKey);
  }
}
